import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { Store } from '@blocksuite/store';

import { isGfxGroupCompatibleModel } from './base.js';
import type { GfxModel } from './model.js';

/**
 * A read of the whole membership of a group or frame while members are not
 * in memory: an action on it loads their parts first.
 */
export class GfxGroupMembersNotLoadedError extends BlockSuiteError {
  constructor(
    readonly groupId: string,
    readonly memberIds: readonly string[],
    readonly partIds: readonly string[] = []
  ) {
    super(
      ErrorCode.GfxBlockElementError,
      `Members ${memberIds.join(', ')} of ${groupId} are not loaded`
    );
    this.name = 'GfxGroupMembersNotLoadedError';
  }
}

type GroupMemberSource = {
  getElementById(id: string): GfxModel | null;
  store: Pick<Store, 'getModelById' | 'pageStructure'>;
};

function memberModels(surface: GroupMemberSource, ids: readonly string[]) {
  const models: GfxModel[] = [];
  const missing: string[] = [];
  for (const id of ids) {
    const model =
      surface.getElementById(id) ??
      (surface.store.getModelById(id) as GfxModel | null);
    if (model) models.push(model);
    else missing.push(id);
  }
  return { models, missing };
}

/** Every member `ids` of the group `groupId`; throws while one is not loaded. */
export function groupMembers(
  groupId: string,
  surface: GroupMemberSource,
  ids: readonly string[]
): GfxModel[] {
  const { models, missing } = memberModels(surface, ids);
  const parts = surface.store.pageStructure.missingMemberParts(groupId);
  if (missing.length || parts.length)
    throw new GfxGroupMembersNotLoadedError(groupId, missing, parts);
  return models;
}

/** The members `ids` in memory, for drawing and layer order only. */
export function loadedGroupMembers(
  surface: GroupMemberSource,
  ids: readonly string[]
): GfxModel[] {
  return memberModels(surface, ids).models;
}

/**
 * Loads the parts of every member of the groups and frames among `elements`,
 * nested ones included; an action on them waits for it. Null when all are in
 * memory, so that the action stays in the caller's transaction.
 */
export function loadGroupMemberParts(
  store: Store,
  elements: readonly GfxModel[],
  signal?: AbortSignal
): Promise<void> | null {
  const groups = elements.filter(isGfxGroupCompatibleModel);
  if (groups.length === 0) return null;
  const structure = store.pageStructure;
  const loading = structure.loadMemberParts(
    groups.map(group => group.id),
    signal
  );
  if (loading) return loading;
  const ids = groups.flatMap(group => structure.descendantMembers(group.id));
  return structure.fieldsLoaded(ids)
    ? null
    : structure.loadPartsOf(ids, signal);
}
